import React, { useState, useEffect } from 'react';
import Layout from '../../../components/layout';
import Seo from '../../../components/seo';
import '../../../styles/startup-ip-valuation.css';
import Disclaimer from '../../../components/disclaimer';

const StartupIPValuation = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 data
    inventions: '',
    competitive_advantage: '',
    public_disclosure: '',
    brand_names: '',
    trademark_status: '',
    trade_secrets: '',
    ip_assignments: '',
    ip_types: [],
    
    // Step 2 data
    industry: '',
    team_size: '',
    company_stage: '',
    revenue: '',
    customers: '',
    funding: '',
    ip_investment: ''
  });
  
  const [leadData, setLeadData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: ''
  });

  const [reportData, setReportData] = useState(null);
  const totalSteps = 5;

  // SEO data
  const seo = {
    metaDesc: 'Founder IP & Startup Valuation Tool - AI-driven IP portfolio analysis and preliminary startup valuation. Discover IP assets, assess protection gaps, and get valuation estimates.'
  };

  // Add no-home class to body on mount
  useEffect(() => {
    document?.body.classList.add('no-home');
    return () => {
      document?.body.classList.remove('no-home');
    };
  }, []);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateIPTypes = (value, checked) => {
    setFormData(prev => ({
      ...prev,
      ip_types: checked 
        ? [...prev.ip_types, value]
        : prev.ip_types.filter(type => type !== value)
    }));
  };

  const updateLeadData = (field, value) => {
    setLeadData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      if (currentStep === 2) {
        // Start analysis after step 2
        setCurrentStep(3);
        setTimeout(() => {
          generateReport();
          setCurrentStep(4);
        }, 3000);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateReport = () => {
    const ipCategories = [
      {
        title: 'Patents & Inventions',
        status: getPatentStatus(),
        items: formData.inventions ? ['Core Technology', 'Competitive Advantages'] : ['No inventions identified']
      },
      {
        title: 'Trademarks & Branding',
        status: getTrademarkStatus(),
        items: formData.brand_names ? [formData.brand_names] : ['No brands identified']
      },
      {
        title: 'Software & Creative Works',
        status: getSoftwareStatus(),
        items: formData.ip_types.length > 0 ? formData.ip_types.map(capitalizeFirst) : ['No software assets identified']
      },
      {
        title: 'Trade Secrets',
        status: getTradeSecretStatus(),
        items: formData.trade_secrets ? ['Confidential Information', 'Business Methods'] : ['No trade secrets identified']
      }
    ];

    const valuationEstimate = calculateValuation();
    const actionSteps = generateActionSteps();

    setReportData({
      ipCategories,
      valuationEstimate,
      actionSteps
    });
  };

  const getPatentStatus = () => {
    if (!formData.inventions || formData.inventions.trim() === '') return 'Critical';
    if (formData.public_disclosure === 'extensive') return 'Critical';
    if (formData.public_disclosure === 'limited') return 'Partial';
    return 'Good';
  };

  const getTrademarkStatus = () => {
    if (!formData.brand_names || formData.brand_names.trim() === '') return 'Critical';
    if (formData.trademark_status === 'registered') return 'Good';
    if (formData.trademark_status === 'pending') return 'Partial';
    return 'Critical';
  };

  const getSoftwareStatus = () => {
    if (formData.ip_types.length === 0) return 'Critical';
    if (formData.ip_types.length >= 2) return 'Good';
    return 'Partial';
  };

  const getTradeSecretStatus = () => {
    if (!formData.trade_secrets || formData.trade_secrets.trim() === '') return 'Critical';
    if (formData.ip_assignments === 'comprehensive') return 'Good';
    if (formData.ip_assignments === 'partial') return 'Partial';
    return 'Critical';
  };

  const calculateValuation = () => {
    let baseValuation = 50000;
    
    const industryMultipliers = {
      'software': 1.5,
      'biotech': 2.0,
      'ai_ml': 1.8,
      'fintech': 1.6,
      'healthcare': 1.7,
      'hardware': 1.4,
      'cleantech': 1.5,
      'consumer': 1.2,
      'b2b': 1.3,
      'other': 1.0
    };

    baseValuation *= industryMultipliers[formData.industry] || 1.0;

    const revenueMultipliers = {
      '0': 1.0,
      '1-10k': 1.2,
      '10-50k': 1.5,
      '50-250k': 2.0,
      '250k-1m': 3.0,
      '1m+': 4.0
    };

    baseValuation *= revenueMultipliers[formData.revenue] || 1.0;

    const teamMultipliers = {
      '1-2': 1.0,
      '3-5': 1.2,
      '6-10': 1.4,
      '11-25': 1.6,
      '25+': 1.8
    };

    baseValuation *= teamMultipliers[formData.team_size] || 1.0;

    let ipBonus = 1.0;
    if (formData.inventions && formData.inventions.trim() !== '') ipBonus += 0.3;
    if (formData.ip_types.length > 0) ipBonus += 0.2;
    if (formData.trade_secrets && formData.trade_secrets.trim() !== '') ipBonus += 0.2;
    if (formData.ip_assignments === 'comprehensive') ipBonus += 0.3;

    baseValuation *= ipBonus;

    return Math.round(baseValuation);
  };

  const generateActionSteps = () => {
    const steps = [];
    
    if (getPatentStatus() !== 'Good') {
      steps.push('File provisional patent applications for your core technology and inventions to establish priority dates.');
    }
    
    if (getTrademarkStatus() !== 'Good') {
      steps.push('Conduct trademark searches and file applications for your company name, product names, and key branding elements.');
    }
    
    if (formData.ip_assignments !== 'comprehensive') {
      steps.push('Implement comprehensive IP assignment agreements for all team members and contractors.');
    }
    
    if (getSoftwareStatus() !== 'Good') {
      steps.push('Document and protect your software code, datasets, and creative works through appropriate IP strategies.');
    }
    
    if (getTradeSecretStatus() !== 'Good') {
      steps.push('Establish formal trade secret protection policies and non-disclosure agreements.');
    }

    steps.push('Schedule a consultation with a patent attorney to develop a comprehensive IP strategy aligned with your business goals.');

    return steps.slice(0, 5);
  };

  const capitalizeFirst = (str) => {
    const words = {
      'software': 'Proprietary Software Code',
      'datasets': 'AI/ML Datasets', 
      'content': 'Original Content/Documentation',
      'designs': 'Product Designs/UI/UX'
    };
    return words[str] || str;
  };

  const submitLeadCapture = () => {
    const { firstName, lastName, email, company } = leadData;

    if (!firstName || !lastName || !email || !company) {
      alert('Please fill in all required fields.');
      return;
    }

    alert('Thank you! Your report has been generated and a consultation request has been submitted. You will receive an email shortly with your complete IP assessment report and next steps.');
    console.log('Lead captured:', { leadData, formData });
  };

  const progress = (currentStep / totalSteps) * 100;

  return (
    // <Layout activeMenu={'ai-tools'}>
    //   <Seo 
    //     title="Founder IP & Startup Valuation Tool - AI-Driven IP Analysis" 
    //     canonical='/ai-tools/startup-ip-valuation' 
    //     seo={seo} 
    //   />
      
      <div className="startup-ip-valuation">
        <div className="startup-ip-container">
          <div className="startup-ip-header">
            <h1>🚀 Founder IP & Startup Valuation Tool</h1>
            <p>Our AI-driven process helps you discover your company's key intellectual property assets, maps your IP protection gaps, and delivers a preliminary estimate of both your IP portfolio value and overall startup valuation.</p>
            
            <div className="startup-ip-disclaimer">
              <strong>⚠️ Important Notice:</strong> This result is for informational planning only. Building real IP value requires detailed legal strategy. Schedule a consultation with a patent attorney at <strong>www.PatentPC.com</strong> for customized IP and valuation insights.
            </div>
          </div>

          <div className="startup-ip-main-content">
            <div className="startup-ip-progress-bar">
              <div className="startup-ip-progress-fill" style={{ width: `${progress}%` }}></div>
            </div>

            <div className="startup-ip-step-indicator">
              {[1, 2, 3, 4, 5].map(step => (
                <div 
                  key={step}
                  className={`startup-ip-step ${step === currentStep ? 'active' : step < currentStep ? 'completed' : ''}`}
                >
                  {step === 1 && '1. IP Discovery'}
                  {step === 2 && '2. Business Info'}
                  {step === 3 && '3. Analysis'}
                  {step === 4 && '4. Report'}
                  {step === 5 && '5. Next Steps'}
                </div>
              ))}
            </div>

            {/* Step 1: IP Asset Discovery */}
            <div className={`startup-ip-form-section ${currentStep === 1 ? 'active' : ''}`}>
              <h2 className="startup-ip-section-title">🔍 Discover Your IP Assets</h2>
              
              <div className="startup-ip-question-group">
                <h3>Inventions & Technology</h3>
                <div className="startup-ip-form-group">
                  <label>What unique technology, process, or invention does your startup use?</label>
                  <textarea 
                    value={formData.inventions}
                    onChange={(e) => updateFormData('inventions', e.target.value)}
                    placeholder="Describe your core technology, algorithms, processes, or innovations..."
                  />
                </div>
                
                <div className="startup-ip-form-group">
                  <label>What makes your solution different from competitors?</label>
                  <textarea 
                    value={formData.competitive_advantage}
                    onChange={(e) => updateFormData('competitive_advantage', e.target.value)}
                    placeholder="Explain your unique competitive advantages..."
                  />
                </div>
                
                <div className="startup-ip-form-group">
                  <label>Have you publicly disclosed any of these inventions? <span className="startup-ip-tooltip" data-tooltip="Public disclosure can affect patent eligibility">ℹ️</span></label>
                  <select 
                    value={formData.public_disclosure}
                    onChange={(e) => updateFormData('public_disclosure', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="no">No public disclosure</option>
                    <option value="limited">Limited disclosure (conferences, articles)</option>
                    <option value="extensive">Extensive public disclosure</option>
                  </select>
                </div>
              </div>

              <div className="startup-ip-question-group">
                <h3>Brand & Trademarks</h3>
                <div className="startup-ip-form-group">
                  <label>Company/Product Names & Branding</label>
                  <input 
                    type="text" 
                    value={formData.brand_names}
                    onChange={(e) => updateFormData('brand_names', e.target.value)}
                    placeholder="List your company name, product names, slogans..."
                  />
                </div>
                
                <div className="startup-ip-form-group">
                  <label>Are any names/logos already trademarked?</label>
                  <select 
                    value={formData.trademark_status}
                    onChange={(e) => updateFormData('trademark_status', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="none">No trademarks filed</option>
                    <option value="pending">Applications pending</option>
                    <option value="registered">Some registered trademarks</option>
                  </select>
                </div>
              </div>

              <div className="startup-ip-question-group">
                <h3>Software & Creative Works</h3>
                <div className="startup-ip-checkbox-group">
                  {[
                    { id: 'software', value: 'software', label: 'Proprietary Software Code' },
                    { id: 'datasets', value: 'datasets', label: 'AI/ML Datasets' },
                    { id: 'content', value: 'content', label: 'Original Content/Documentation' },
                    { id: 'designs', value: 'designs', label: 'Product Designs/UI/UX' }
                  ].map(item => (
                    <div key={item.id} className="startup-ip-checkbox-item">
                      <input 
                        type="checkbox" 
                        id={item.id}
                        checked={formData.ip_types.includes(item.value)}
                        onChange={(e) => updateIPTypes(item.value, e.target.checked)}
                      />
                      <label htmlFor={item.id}>{item.label}</label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="startup-ip-question-group">
                <h3>Trade Secrets & Confidential Info</h3>
                <div className="startup-ip-form-group">
                  <label>What confidential information gives you an edge? <span className="startup-ip-tooltip" data-tooltip="Trade secrets must be kept confidential to maintain protection">ℹ️</span></label>
                  <textarea 
                    value={formData.trade_secrets}
                    onChange={(e) => updateFormData('trade_secrets', e.target.value)}
                    placeholder="Customer lists, formulas, processes, business methods..."
                  />
                </div>
                
                <div className="startup-ip-form-group">
                  <label>Do you have NDAs and employee IP assignments in place?</label>
                  <select 
                    value={formData.ip_assignments}
                    onChange={(e) => updateFormData('ip_assignments', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="none">No formal agreements</option>
                    <option value="partial">Some agreements in place</option>
                    <option value="comprehensive">Comprehensive IP assignments</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Step 2: Business Information */}
            <div className={`startup-ip-form-section ${currentStep === 2 ? 'active' : ''}`}>
              <h2 className="startup-ip-section-title">📊 Business & Market Information</h2>
              
              <div className="startup-ip-question-group">
                <h3>Company Basics</h3>
                <div className="startup-ip-form-group">
                  <label>Industry/Sector</label>
                  <select 
                    value={formData.industry}
                    onChange={(e) => updateFormData('industry', e.target.value)}
                  >
                    <option value="">Select your industry...</option>
                    <option value="software">Software/SaaS</option>
                    <option value="biotech">Biotech/Pharmaceuticals</option>
                    <option value="hardware">Hardware/IoT</option>
                    <option value="fintech">Fintech</option>
                    <option value="healthcare">Healthcare/Medtech</option>
                    <option value="ai_ml">AI/Machine Learning</option>
                    <option value="cleantech">Cleantech/Energy</option>
                    <option value="consumer">Consumer Products</option>
                    <option value="b2b">B2B Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="startup-ip-form-group">
                  <label>Team Size</label>
                  <select 
                    value={formData.team_size}
                    onChange={(e) => updateFormData('team_size', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="1-2">1-2 founders</option>
                    <option value="3-5">3-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="11-25">11-25 people</option>
                    <option value="25+">25+ people</option>
                  </select>
                </div>
                
                <div className="startup-ip-form-group">
                  <label>Company Stage</label>
                  <select 
                    value={formData.company_stage}
                    onChange={(e) => updateFormData('company_stage', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="idea">Idea/Concept stage</option>
                    <option value="prototype">Prototype/MVP</option>
                    <option value="early_customers">Early customers</option>
                    <option value="growing_revenue">Growing revenue</option>
                    <option value="scaling">Scaling</option>
                  </select>
                </div>
              </div>

              <div className="startup-ip-question-group">
                <h3>Traction & Revenue</h3>
                <div className="startup-ip-form-group">
                  <label>Annual Revenue (if any)</label>
                  <select 
                    value={formData.revenue}
                    onChange={(e) => updateFormData('revenue', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="0">No revenue yet</option>
                    <option value="1-10k">$1K - $10K</option>
                    <option value="10-50k">$10K - $50K</option>
                    <option value="50-250k">$50K - $250K</option>
                    <option value="250k-1m">$250K - $1M</option>
                    <option value="1m+">$1M+</option>
                  </select>
                </div>
                
                <div className="startup-ip-form-group">
                  <label>Number of Customers/Users</label>
                  <select 
                    value={formData.customers}
                    onChange={(e) => updateFormData('customers', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="0-10">0-10</option>
                    <option value="10-100">10-100</option>
                    <option value="100-1k">100-1,000</option>
                    <option value="1k-10k">1,000-10,000</option>
                    <option value="10k+">10,000+</option>
                  </select>
                </div>
              </div>

              <div className="startup-ip-question-group">
                <h3>Funding & Investment</h3>
                <div className="startup-ip-form-group">
                  <label>Previous Funding Raised</label>
                  <select 
                    value={formData.funding}
                    onChange={(e) => updateFormData('funding', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="0">Bootstrapped/No funding</option>
                    <option value="under-50k">Under $50K</option>
                    <option value="50-250k">$50K - $250K</option>
                    <option value="250k-1m">$250K - $1M</option>
                    <option value="1-5m">$1M - $5M</option>
                    <option value="5m+">$5M+</option>
                  </select>
                </div>
                
                <div className="startup-ip-form-group">
                  <label>R&D and IP Investment to Date</label>
                  <select 
                    value={formData.ip_investment}
                    onChange={(e) => updateFormData('ip_investment', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="under-10k">Under $10K</option>
                    <option value="10-50k">$10K - $50K</option>
                    <option value="50-100k">$50K - $100K</option>
                    <option value="100-500k">$100K - $500K</option>
                    <option value="500k+">$500K+</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Step 3: AI Analysis */}
            <div className={`startup-ip-form-section ${currentStep === 3 ? 'active' : ''}`}>
              <h2 className="startup-ip-section-title">🤖 AI Analysis in Progress</h2>
              <div className="startup-ip-analysis-section">
                <div>⚡</div>
                <h3>Analyzing Your IP Portfolio...</h3>
                <p>Our AI is processing your information to identify IP assets, assess protection gaps, and calculate preliminary valuations.</p>
                <div className="startup-ip-analysis-progress">
                  <div className="startup-ip-analysis-progress-bar" style={{ width: '100%' }}></div>
                </div>
              </div>
            </div>

            {/* Step 4: Report */}
            <div className={`startup-ip-form-section ${currentStep === 4 ? 'active' : ''}`}>
              <h2 className="startup-ip-section-title">📋 Your IP & Valuation Report</h2>
              
              {reportData && (
                <>
                  <div className="startup-ip-report-section">
                    <h3>🗺️ IP Portfolio Map</h3>
                    <div className="startup-ip-portfolio-map">
                      {reportData.ipCategories.map((category, index) => (
                        <div key={index} className="startup-ip-category">
                          <h4>{category.title}</h4>
                          <div className={`startup-ip-status-badge startup-ip-status-${category.status.toLowerCase()}`}>
                            {category.status}
                          </div>
                          <ul style={{ marginTop: '15px', paddingLeft: '20px' }}>
                            {category.items.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="startup-ip-valuation-summary">
                    <h3>💰 Preliminary Valuation Estimate</h3>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: '20px 0' }}>
                      ${reportData.valuationEstimate.toLocaleString()} - ${(reportData.valuationEstimate * 2).toLocaleString()}
                    </div>
                    <p><strong>Important:</strong> This is a preliminary, non-binding estimate for planning purposes only.</p>
                  </div>

                  <div className="startup-ip-action-items">
                    <h3>🎯 Recommended Action Steps</h3>
                    <ol>
                      {reportData.actionSteps.map((step, index) => (
                        <li key={index}>{step}</li>
                      ))}
                    </ol>
                  </div>
                </>
              )}
            </div>

            {/* Step 5: Lead Capture */}
            <div className={`startup-ip-form-section ${currentStep === 5 ? 'active' : ''}`}>
              <div className="startup-ip-lead-capture">
                <h3>🚀 Get Your Complete Report & Expert Consultation</h3>
                <p>Enter your details below to download your complete IP assessment report and schedule a consultation with our patent attorneys.</p>
                
                <div className="startup-ip-lead-form">
                  <input 
                    type="text" 
                    placeholder="First Name" 
                    value={leadData.firstName}
                    onChange={(e) => updateLeadData('firstName', e.target.value)}
                    required 
                  />
                  <input 
                    type="text" 
                    placeholder="Last Name" 
                    value={leadData.lastName}
                    onChange={(e) => updateLeadData('lastName', e.target.value)}
                    required 
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address" 
                    value={leadData.email}
                    onChange={(e) => updateLeadData('email', e.target.value)}
                    required 
                  />
                  <input 
                    type="text" 
                    placeholder="Company Name" 
                    value={leadData.company}
                    onChange={(e) => updateLeadData('company', e.target.value)}
                    required 
                  />
                  <input 
                    type="tel" 
                    placeholder="Phone Number" 
                    value={leadData.phone}
                    onChange={(e) => updateLeadData('phone', e.target.value)}
                  />
                </div>
                
                <button 
                  className="startup-ip-btn startup-ip-btn-primary" 
                  onClick={submitLeadCapture}
                  style={{ marginTop: '20px', background: 'white', color: '#ff6b6b', fontSize: '1.2rem' }}
                >
                  Download Report & Schedule Consultation
                </button>
                
                <p style={{ marginTop: '20px', fontSize: '0.9rem', opacity: '0.9' }}>
                  By submitting, you agree to receive communications from PatentPC.com regarding IP strategy and legal services.
                </p>
              </div>

              <div className="startup-ip-consultation-benefits">
                <h3>Why Work with Patent Attorneys?</h3>
                <div className="startup-ip-benefits-grid">
                  <div>
                    <h4>🛡️ Maximize Protection</h4>
                    <p>Ensure comprehensive IP coverage and avoid costly gaps that competitors could exploit.</p>
                  </div>
                  <div>
                    <h4>💎 Increase Valuation</h4>
                    <p>Properly protected IP can significantly boost your startup's valuation and investor appeal.</p>
                  </div>
                  <div>
                    <h4>⚡ Strategic Timing</h4>
                    <p>File at the right time to maximize protection while managing costs effectively.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="startup-ip-navigation">
              {currentStep > 1 && currentStep !== 3 && (
                <button className="startup-ip-btn startup-ip-btn-secondary" onClick={previousStep}>
                  ← Previous
                </button>
              )}
              {currentStep < totalSteps && currentStep !== 3 && currentStep !== 5 && (
                <button className="startup-ip-btn startup-ip-btn-primary" onClick={nextStep}>
                  {currentStep === 4 ? 'Get My Report →' : 'Next Step →'}
                </button>
              )}
            </div>
          </div>

          {/* Footer Disclaimer */}
          <div className="startup-ip-footer-disclaimer" style={{ color: 'white'}}>
            <h3>⚠️ Important Legal Disclaimer</h3>
            <p style={{ color: 'white'}}>Please note that all assessment results are preliminary and may not be confidential if not using a paid AI account with the proper safeguards so that AI will not train on the data. The app is provided free of charge and there is no attorney client relationship by using this app.</p>
            <p style={{ color: 'white'}}>Further, proper creation and capture of IP value require tailored legal and valuation strategies—founders should consult a qualified patent attorney to develop a robust IP portfolio and maximize startup valuation.</p>
            <p style={{ color: 'white'}}>Please visit <a href="https://patentpc.com" target="_blank" rel="noopener noreferrer">PatentPC.com</a> to consult with a patent lawyer.</p>
          </div>

          <Disclaimer />
        </div>
      </div>
    // </Layout>
  );
};

export default StartupIPValuation;
